"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  PanelRightClose,
  Target,
  TrendingUp,
  AlertCircle,
  Brain,
  HelpCircle,
  Pencil,
  Plus,
  X,
  Check,
  ExternalLink,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { ThinkingVisual } from "@/components/ui/thinking-visual"
import { CollapsedRightRail } from "@/components/rail/collapsed-right-rail"
import { useGoals } from "@/hooks/use-goals"
import type { GoalSignal, GoalConstraint } from "@/hooks/use-goals"

function getGoalsRailOpenState(): boolean {
  if (typeof window === "undefined") return true
  try {
    const value = localStorage.getItem("logosiq.goalsRailOpen")
    return value === null ? true : value === "true"
  } catch {
    return true
  }
}

function setGoalsRailOpenState(isOpen: boolean): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem("logosiq.goalsRailOpen", isOpen.toString())
  } catch {
    // Silently fail
  }
}

// Advisor questions that suggest clarifying discussions
const ADVISOR_QUESTIONS = [
  { id: "1", text: "What does success look like in 90 days?" },
  { id: "2", text: "What constraints should I factor in?" },
  { id: "3", text: "Which metrics matter most right now?" },
  { id: "4", text: "What's your biggest risk this quarter?" },
]

interface GoalsRailProps {
  className?: string
}

export function GoalsRail({ className }: GoalsRailProps) {
  const [railOpen, setRailOpen] = useState<boolean>(true)
  const [isEditingGoal, setIsEditingGoal] = useState(false)
  const [editTitle, setEditTitle] = useState("")
  const [editDescription, setEditDescription] = useState("")
  
  // Signals editing
  const [isEditingSignals, setIsEditingSignals] = useState(false)
  const [newSignalLabel, setNewSignalLabel] = useState("")
  const [newSignalValue, setNewSignalValue] = useState("")
  
  // Constraints editing
  const [isEditingConstraints, setIsEditingConstraints] = useState(false)
  const [newConstraintText, setNewConstraintText] = useState("")

  const {
    activeGoal,
    isLoading,
    signals,
    addSignal,
    removeSignal,
    constraints,
    addConstraint,
    removeConstraint,
    relatedMemories,
    updateGoal,
  } = useGoals()

  useEffect(() => {
    const storedState = getGoalsRailOpenState()
    setRailOpen(storedState)
  }, [])

  useEffect(() => {
    if (activeGoal) {
      setEditTitle(activeGoal.title)
      setEditDescription(activeGoal.description || "")
    }
  }, [activeGoal])

  const handleOpenRail = () => {
    setRailOpen(true)
    setGoalsRailOpenState(true)
  }

  const handleCloseRail = () => {
    setRailOpen(false)
    setGoalsRailOpenState(false)
  }

  const handleSaveGoal = async () => {
    if (!editTitle.trim()) return
    try {
      await updateGoal(editTitle.trim(), editDescription.trim() || null)
      setIsEditingGoal(false)
    } catch (err) {
      console.error("[GoalsRail] Failed to save goal:", err)
    }
  }

  const handleAddSignal = () => {
    if (!newSignalLabel.trim() || !newSignalValue.trim()) return
    addSignal(newSignalLabel.trim(), newSignalValue.trim())
    setNewSignalLabel("")
    setNewSignalValue("")
  }

  const handleAddConstraint = () => {
    if (!newConstraintText.trim()) return
    addConstraint(newConstraintText.trim())
    setNewConstraintText("")
  }

  if (!railOpen) {
    return <CollapsedRightRail onOpen={handleOpenRail} ariaLabel="Open Goals panel" />
  }

  return (
    <aside
      className={cn(
        "fixed lg:static",
        "inset-y-0 right-0 z-50 lg:z-0",
        "flex flex-col",
        "lg:flex",
        railOpen ? "w-80 lg:w-[340px]" : "lg:w-0 lg:opacity-0 lg:pointer-events-none",
        "min-h-full",
        "bg-background lg:bg-[var(--surface-context)]",
        railOpen && "lg:shadow-[inset_4px_0_8px_rgba(0,0,0,0.18)]",
        className,
      )}
      style={{ flexShrink: 0 }}
    >
      {/* Header */}
      <div className="hidden lg:flex h-16 items-center justify-between px-4 pb-1 shrink-0">
        <div className="flex items-center gap-3">
          <div className="scale-[0.70]">
            <ThinkingVisual
              config={{
                active: true,
                breathMode: "calm",
                visualEmphasis: "normal",
                motionComplexity: "still",
                version: "1.1",
              }}
              context="inline"
            />
          </div>
          <span className="text-[22px] font-display font-normal text-foreground">Goals</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 rounded-lg"
          onClick={handleCloseRail}
          aria-label="Close goals panel"
        >
          <PanelRightClose className="h-4 w-4 text-[var(--text-secondary)]" />
        </Button>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-hidden min-h-0">
        <div className="h-full overflow-y-auto p-4 space-y-6">
          {/* A. Primary Goal (editable) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <Target className="h-3 w-3" />
                Primary Goal
              </h3>
              {activeGoal && !isEditingGoal && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setIsEditingGoal(true)}
                >
                  <Pencil className="h-3 w-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {isLoading ? (
              <div className="p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
                <div className="animate-pulse h-4 bg-muted rounded w-3/4" />
              </div>
            ) : activeGoal ? (
              <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                {isEditingGoal ? (
                  <div className="space-y-3">
                    <Input
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Goal title"
                      className="text-sm"
                    />
                    <Input
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Optional description"
                      className="text-sm"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleSaveGoal} className="h-7">
                        <Check className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setIsEditingGoal(false)
                          setEditTitle(activeGoal.title)
                          setEditDescription(activeGoal.description || "")
                        }}
                        className="h-7"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm font-medium text-foreground">{activeGoal.title}</p>
                    {activeGoal.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {activeGoal.description}
                      </p>
                    )}
                    {activeGoal.target_date && (
                      <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-muted text-xs text-muted-foreground">
                        90-day target
                      </span>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
                <p className="text-sm text-muted-foreground italic">Not set yet</p>
                <Link
                  href="/advisor?new=1&from=goals&scope=90_day_goal"
                  className="inline-flex items-center mt-2 text-xs text-primary hover:underline"
                >
                  Set goal in Advisor
                  <ExternalLink className="h-3 w-3 ml-1" />
                </Link>
              </div>
            )}
          </div>

          {/* B. Success Signals (new, lightweight metrics) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <TrendingUp className="h-3 w-3" />
                Success Signals
              </h3>
              {!isEditingSignals && signals.length < 3 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setIsEditingSignals(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
            </div>
            
            <div className="p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] space-y-2">
              {signals.length === 0 && !isEditingSignals ? (
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground">No signals set</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-7 text-xs text-primary"
                    onClick={() => setIsEditingSignals(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add signal
                  </Button>
                </div>
              ) : (
                <>
                  {signals.map((signal) => (
                    <div
                      key={signal.id}
                      className="flex items-center justify-between py-1 border-b border-[var(--border)] last:border-0"
                    >
                      <span className="text-xs text-muted-foreground">{signal.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground">{signal.value}</span>
                        {isEditingSignals && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => removeSignal(signal.id)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </>
              )}
              
              {isEditingSignals && signals.length < 3 && (
                <div className="pt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={newSignalLabel}
                      onChange={(e) => setNewSignalLabel(e.target.value)}
                      placeholder="Label (e.g., MRR)"
                      className="text-xs h-8 flex-1"
                    />
                    <Input
                      value={newSignalValue}
                      onChange={(e) => setNewSignalValue(e.target.value)}
                      placeholder="Value"
                      className="text-xs h-8 w-20"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={handleAddSignal}>
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => setIsEditingSignals(false)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* C. Constraints (editable, planning-only) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <AlertCircle className="h-3 w-3" />
                Constraints
              </h3>
              {!isEditingConstraints && constraints.length < 6 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => setIsEditingConstraints(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add
                </Button>
              )}
            </div>
            
            <div className="p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
              {constraints.length === 0 && !isEditingConstraints ? (
                <div className="text-center py-2">
                  <p className="text-sm text-muted-foreground">No constraints set</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-1 h-7 text-xs text-primary"
                    onClick={() => setIsEditingConstraints(true)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add constraint
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {constraints.map((constraint) => (
                    <span
                      key={constraint.id}
                      className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs",
                        "bg-amber-500/10 text-amber-200 border border-amber-500/20"
                      )}
                    >
                      {constraint.text}
                      {isEditingConstraints && (
                        <button
                          onClick={() => removeConstraint(constraint.id)}
                          className="ml-1 hover:text-amber-100"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              
              {isEditingConstraints && constraints.length < 6 && (
                <div className="pt-3 space-y-2">
                  <Input
                    value={newConstraintText}
                    onChange={(e) => setNewConstraintText(e.target.value)}
                    placeholder="e.g., No hiring this quarter"
                    className="text-xs h-8"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleAddConstraint()
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs" onClick={handleAddConstraint}>
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs"
                      onClick={() => setIsEditingConstraints(false)}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* D. Related Memories (read-only) */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Brain className="h-3 w-3" />
              Related Memories
            </h3>
            <div className="p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)]">
              {relatedMemories.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Linked automatically once you approve memories
                </p>
              ) : (
                <div className="space-y-2">
                  {relatedMemories.slice(0, 5).map((memory) => (
                    <Link
                      key={memory.id}
                      href={`/brain?memory=${memory.id}`}
                      className={cn(
                        "flex items-center gap-2 px-2 py-1.5 rounded-md",
                        "text-xs text-[var(--brain)] hover:bg-[var(--brain)]/10",
                        "ring-1 ring-[var(--brain)]/30 transition-colors"
                      )}
                    >
                      <Brain className="h-3 w-3 shrink-0" />
                      <span className="truncate">{memory.title}</span>
                    </Link>
                  ))}
                  {relatedMemories.length > 0 && (
                    <Link
                      href="/brain"
                      className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground mt-2"
                    >
                      View all in Brain
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* E. Advisor Questions (read-only prompts) */}
          <div className="space-y-2">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <HelpCircle className="h-3 w-3" />
              Advisor Questions
            </h3>
            <div className="p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] space-y-2">
              {ADVISOR_QUESTIONS.slice(0, 4).map((question) => (
                <Link
                  key={question.id}
                  href={`/advisor?new=1&prompt=${encodeURIComponent(question.text)}`}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-md",
                    "text-xs text-muted-foreground hover:text-foreground",
                    "bg-[var(--surface-hover)] hover:bg-[var(--surface-hover)]/80",
                    "transition-colors"
                  )}
                >
                  <span className="line-clamp-1">{question.text}</span>
                  <span className="text-primary shrink-0 ml-2">Answer</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
